/**
 * Subscription and payments (Phase 13, plan §3A). Card data never touches the SPA: checkout,
 * card capture (Checkout in setup mode) and the customer portal are Stripe-hosted pages, reached
 * with `redirectTo(url)`.
 */

import { apiClient } from '../api-client';
import type {
  InvoicesResponse,
  PlanChangeEffective,
  PlanChangePreviewDto,
  PlanDto,
  PlanId,
  PlansResponse,
  RedirectUrlResponse,
  SamplePatientResponse,
  SubscriptionDto,
} from '../../dtos/billing/subscription.dto';
import { normalizeCapUsage, normalizePlanTerms, normalizeUsage } from '../../utils/subscription';

function requireData<T>(data: T | undefined, what: string): T {
  if (data === undefined || data === null) {
    throw new Error(`${what} returned no data`);
  }
  return data;
}

function requireUrl(data: RedirectUrlResponse | undefined, what: string): string {
  const url = requireData(data, what).url;
  if (typeof url !== 'string' || !url) {
    throw new Error(`${what} did not return a link`);
  }
  return url;
}

const PREVIEW_EFFECTIVE: readonly PlanChangeEffective[] = ['trial', 'now', 'period_end'];

const toNumberOrNull = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value) ? value : null);

/** Phase 21 plan fields default to "no usage pricing" for an older backend. */
function normalizePlan(plan: PlanDto): PlanDto {
  return {
    ...plan,
    patientCap: toNumberOrNull(plan.patientCap),
    includedPatients: toNumberOrNull(plan.includedPatients),
    perPatientCents: toNumberOrNull(plan.perPatientCents),
  };
}

/** Phase 21 fields (cap usage, usage, plan terms, founding flag) default safely for an older backend. */
function normalizeSubscriptionDto(data: SubscriptionDto): SubscriptionDto {
  return {
    ...data,
    cap: normalizeCapUsage(data.cap),
    usage: normalizeUsage(data.usage),
    planTerms: normalizePlanTerms(data.planTerms),
    foundingAgency: data.foundingAgency === true,
  };
}

export class SubscriptionService {
  /** Public plan catalog (no JWT): also loaded on the sign-up page, before anyone is signed in. */
  static async getPlans(): Promise<PlansResponse> {
    const response = await apiClient.get<PlansResponse>('/plans', undefined, { skipAuth: true });
    const data = requireData(response.data, 'Plans');
    return {
      plans: Array.isArray(data.plans) ? data.plans.map(normalizePlan) : [],
      taxNote: typeof data.taxNote === 'string' && data.taxNote ? data.taxNote : null,
    };
  }

  static async getSubscription(): Promise<SubscriptionDto> {
    const response = await apiClient.get<SubscriptionDto>('/subscription');
    return normalizeSubscriptionDto(requireData(response.data, 'Subscription'));
  }

  static async getInvoices(): Promise<InvoicesResponse> {
    const response = await apiClient.get<InvoicesResponse>('/subscription/invoices');
    const data = requireData(response.data, 'Billing history');
    return {
      invoices: Array.isArray(data.invoices) ? data.invoices : [],
      pendingCharges: Array.isArray(data.pendingCharges) ? data.pendingCharges : [],
    };
  }

  /** Start Stripe Checkout for a plan; returns the hosted checkout URL. */
  static async checkout(plan: PlanId): Promise<string> {
    const response = await apiClient.post<RedirectUrlResponse>('/subscription/checkout', { plan });
    return requireUrl(response.data, 'Checkout');
  }

  /** Upgrade (now, prorated) or downgrade (at period end). 409 DOWNGRADE_OVER_CAP lists the patients. */
  static async changePlan(plan: PlanId): Promise<SubscriptionDto> {
    const response = await apiClient.post<SubscriptionDto>('/subscription/change', { plan });
    return normalizeSubscriptionDto(requireData(response.data, 'Plan change'));
  }

  /** Preview a plan change: amount due now (prorated, with tax) and the next invoice. */
  static async previewChange(plan: PlanId): Promise<PlanChangePreviewDto> {
    const response = await apiClient.get<PlanChangePreviewDto>('/subscription/change/preview', { plan });
    const data = requireData(response.data, 'Plan change preview');
    if (!PREVIEW_EFFECTIVE.includes(data.effective)) {
      throw new Error('Plan change preview returned no effective date');
    }
    return {
      plan: data.plan,
      effective: data.effective,
      dueNowCents: toNumberOrNull(data.dueNowCents) ?? 0,
      taxCents: toNumberOrNull(data.taxCents) ?? 0,
      nextInvoiceCents: toNumberOrNull(data.nextInvoiceCents),
      nextInvoiceAt: typeof data.nextInvoiceAt === 'string' && data.nextInvoiceAt ? data.nextInvoiceAt : null,
      includedPatients: toNumberOrNull(data.includedPatients),
      perPatientCents: toNumberOrNull(data.perPatientCents),
      currentBillable: toNumberOrNull(data.currentBillable),
      projectedUsageCents: toNumberOrNull(data.projectedUsageCents),
    };
  }

  /** Keep the current plan: cancel a scheduled downgrade. 409 INVALID_STATE when nothing is pending. */
  static async undoChange(): Promise<SubscriptionDto> {
    const response = await apiClient.post<SubscriptionDto>('/subscription/change/undo');
    return normalizeSubscriptionDto(requireData(response.data, 'Keep plan'));
  }

  /** Add or replace the card (Stripe Checkout in setup mode); returns the hosted page URL. */
  static async addPaymentMethod(): Promise<string> {
    const response = await apiClient.post<RedirectUrlResponse>('/subscription/payment-method');
    return requireUrl(response.data, 'Card page');
  }

  /** Open the Stripe customer portal (billing details and invoices); returns the portal URL. */
  static async portal(): Promise<string> {
    const response = await apiClient.post<RedirectUrlResponse>('/subscription/portal');
    return requireUrl(response.data, 'Customer portal');
  }

  /** Cancel at the end of the current period (no refund). */
  static async cancel(): Promise<SubscriptionDto> {
    const response = await apiClient.post<SubscriptionDto>('/subscription/cancel');
    return normalizeSubscriptionDto(requireData(response.data, 'Cancel'));
  }

  static async undoCancel(): Promise<SubscriptionDto> {
    const response = await apiClient.post<SubscriptionDto>('/subscription/cancel/undo');
    return normalizeSubscriptionDto(requireData(response.data, 'Undo cancel'));
  }

  /** Load the fixed sample patient (made-up data, not counted toward the cap). 409 SAMPLE_EXISTS. */
  static async loadSamplePatient(): Promise<string> {
    const response = await apiClient.post<SamplePatientResponse>('/patients/sample');
    const patientId = requireData(response.data, 'Sample patient').patientId;
    if (typeof patientId !== 'string' || !patientId) {
      throw new Error('Sample patient did not return an ID');
    }
    return patientId;
  }

  /** Hard-delete the sample patient. 409 NOT_SAMPLE for any real patient. */
  static async deleteSamplePatient(patientId: string): Promise<void> {
    await apiClient.delete<void>(`/patients/${encodeURIComponent(patientId)}/sample`);
  }

  /** Leave the SPA for a Stripe-hosted page. */
  static redirectTo(url: string): void {
    window.location.assign(url);
  }
}

export default SubscriptionService;
